import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { useAppContext } from '~/contexts';
import { DeviceStatus } from '~/generated/graphql';
import { formatTemperature } from '~/lib/units';
import { DeviceHealthDocument } from '../queries/device-health.generated';
import { DeviceStatsDocument } from '../queries/device-stats.generated';
import { HighTemperatureAlertsDocument } from '../queries/high-temperature-alerts.generated';
import { LowStorageAlertsDocument } from '../queries/low-storage-alerts.generated';
import { RecentActivityDocument } from '../queries/recent-activity.generated';
import { UptimeAlertsDocument } from '../queries/uptime-alerts.generated';

export type StatusFilter = 'online' | 'offline' | 'all';

const STATUS_FILTER_MAP: Record<StatusFilter, DeviceStatus[]> = {
  online: [DeviceStatus.Healthy],
  offline: [DeviceStatus.Warn, DeviceStatus.Error],
  all: [DeviceStatus.Healthy, DeviceStatus.Warn, DeviceStatus.Error],
};

export const useOperationalData = () => {
  const { currentNetwork, currentUser } = useAppContext();
  const isSysadmin = currentUser.admin;

  // Threshold state - persisted to localStorage
  const [storageThreshold, setStorageThreshold] = useLocalStorage('dashboard:storageThreshold', 10);
  const [temperatureThreshold, setTemperatureThreshold] = useLocalStorage(
    'dashboard:temperatureThreshold',
    66,
  );
  const [restartHours, setRestartHours] = useLocalStorage('dashboard:restartHours', 4);
  const [storageStatusSelection, setStorageStatusSelection] = useLocalStorage<StatusFilter>(
    'dashboard:storageStatus',
    'online',
  );
  const [tempStatusSelection, setTempStatusSelection] = useLocalStorage<StatusFilter>(
    'dashboard:tempStatus',
    'online',
  );
  const [uptimeStatusSelection, setUptimeStatusSelection] = useLocalStorage<StatusFilter>(
    'dashboard:uptimeStatus',
    'online',
  );

  const storageStatusFilter = STATUS_FILTER_MAP[storageStatusSelection];
  const tempStatusFilter = STATUS_FILTER_MAP[tempStatusSelection];
  const uptimeStatusFilter = STATUS_FILTER_MAP[uptimeStatusSelection];

  // Queries
  const { data: deviceStatsData } = useQuery(DeviceStatsDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const { data: deviceHealthData, loading: deviceHealthLoading } = useQuery(DeviceHealthDocument, {
    variables: { networkId: currentNetwork.id },
    skip: !isSysadmin,
  });

  const { data: recentActivityData, loading: recentActivityLoading } = useQuery(
    RecentActivityDocument,
    { variables: { networkId: currentNetwork.id } },
  );

  const { data: lowStorageData, loading: lowStorageLoading } = useQuery(LowStorageAlertsDocument, {
    variables: { networkId: currentNetwork.id, storageThreshold, status: storageStatusFilter },
    fetchPolicy: 'no-cache',
    skip: !isSysadmin,
  });

  const { data: highTempData, loading: highTempLoading } = useQuery(HighTemperatureAlertsDocument, {
    variables: { networkId: currentNetwork.id, temperatureThreshold, status: tempStatusFilter },
    fetchPolicy: 'no-cache',
    skip: !isSysadmin,
  });

  const { data: uptimeData, loading: uptimeLoading } = useQuery(UptimeAlertsDocument, {
    variables: {
      networkId: currentNetwork.id,
      uptimeHours: restartHours,
      status: uptimeStatusFilter,
    },
    fetchPolicy: 'no-cache',
    skip: !isSysadmin,
  });

  // Extract device stats
  const deviceStats = deviceStatsData?.network?.deviceStats;
  const totalDevices = deviceStats?.activeCount ?? 0;
  const onlineDevices = deviceStats?.healthyCount ?? 0;
  const offlineDevices = deviceStats?.errorCount ?? 0;
  const warningDevices = deviceStats?.warnCount ?? 0;
  const silencedDevices = deviceStats?.silenceCount ?? 0;
  const displayOnCount = deviceStats?.displayOnCount ?? 0;
  const displayOffCount = deviceStats?.displayOffCount ?? 0;

  // License stats
  const shouldShowLicenses =
    currentNetwork.canManage.value && deviceStatsData?.network?.deviceLicenses != null;
  const deviceLicenses = deviceStatsData?.network?.deviceLicenses ?? 0;
  const deviceLicensesUsed = deviceStatsData?.network?.deviceLicensesUsed ?? 0;

  // Device health data
  const deviceCountsByKind = useMemo(
    () => deviceHealthData?.network?.deviceFilterInfo.kind ?? [],
    [deviceHealthData?.network?.deviceFilterInfo.kind],
  );
  const deviceGroups = useMemo(
    () => deviceHealthData?.network?.allDeviceGroups ?? [],
    [deviceHealthData?.network?.allDeviceGroups],
  );

  // Health by device group
  const groupHealth = useMemo(() => {
    return deviceGroups.map((group) => ({
      id: group.id,
      name: group.name,
      totalDevices: group.deviceCount,
      onlineCount: group.deviceStats.healthyCount,
    }));
  }, [deviceGroups]);

  // Low storage devices
  const lowStorageDevices = useMemo(() => {
    return (lowStorageData?.network?.lowStorageDevices.nodes ?? []).map((device) => {
      const usedPercent =
        device.diskTotal != null && device.diskTotal > 0
          ? Math.round(((device.diskTotal - (device.diskAvailable ?? 0)) / device.diskTotal) * 100)
          : 0;
      const availablePercent = 100 - usedPercent;
      return {
        ...device,
        value: `${availablePercent}%`,
      };
    });
  }, [lowStorageData?.network?.lowStorageDevices.nodes]);

  // High temperature devices
  const highTempDevices = useMemo(() => {
    return (highTempData?.network?.highTempDevices.nodes ?? []).map((device) => ({
      ...device,
      value: formatTemperature(device.cpuTemperature ?? 0),
    }));
  }, [highTempData?.network?.highTempDevices.nodes]);

  const recentRestartCount =
    (uptimeData?.network?.recentReboots.pageInfo.nodesCount ?? 0) +
    (uptimeData?.network?.recentRestarts.pageInfo.nodesCount ?? 0);

  return {
    // Stats
    totalDevices,
    onlineDevices,
    offlineDevices,
    warningDevices,
    silencedDevices,
    displayOnCount,
    displayOffCount,

    // Licenses
    shouldShowLicenses,
    deviceLicenses,
    deviceLicensesUsed,

    // Device health
    deviceCountsByKind,
    groupHealth,
    deviceHealthLoading,

    // Alerts
    lowStorageDevices,
    lowStorageLoading,
    lowStorageCount: lowStorageData?.network?.lowStorageDevices.pageInfo.nodesCount ?? 0,
    highTempDevices,
    highTempLoading,
    highTempCount: highTempData?.network?.highTempDevices.pageInfo.nodesCount ?? 0,

    // Uptime (sysadmin)
    isSysadmin,
    uptimeData,
    uptimeLoading,
    recentRestartCount,

    // Recent activity
    recentActivityData,
    recentActivityLoading,

    // Thresholds
    storageThreshold,
    setStorageThreshold,
    temperatureThreshold,
    setTemperatureThreshold,
    restartHours,
    setRestartHours,

    // Alert status filters
    storageStatusSelection,
    setStorageStatusSelection,
    tempStatusSelection,
    setTempStatusSelection,
    uptimeStatusSelection,
    setUptimeStatusSelection,

    // Permissions
    canManage: currentNetwork.canManage.value,
  };
};
